import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Close, Add } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { useShareTemplates } from "../../../configurations/share-messages/hooks/useShareMessageTemplates";
import { ShareTemplate } from "../../../configurations/share-messages/types/share-template.types";
import CreateTemplateModal from "../../../configurations/share-messages/components/CreateTemplateModal";

interface SelectTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: ShareTemplate) => void;
  serviceType?: string;
}

const SelectTemplateModal: React.FC<SelectTemplateModalProps> = ({
  open,
  onClose,
  onSelectTemplate,
  serviceType,
}) => {
  const theme = useSelector((state: RootState) => state.theme);
  const { templates, loading, fetchTemplatesByCategory, deleteTemplate } =
    useShareTemplates();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const getCategoryFromServiceType = (type?: string): string => {
    switch (type) {
      case "Funeral":
      case "FuneralHome":
        return "Funeral";
      case "Church":
        return "Church";
      case "Conference":
        return "Conference";
      default:
        return "Funeral";
    }
  };

  useEffect(() => {
    if (open) {
      const category = getCategoryFromServiceType(serviceType);
      fetchTemplatesByCategory(category);
    }
  }, [open, serviceType, fetchTemplatesByCategory]);

  const handleSelectAndClose = () => {
    const selectedTemplate = templates.find((t) => t.id === selectedId);
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose();
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Funeral":
        return theme.primary_colors[400];
      case "Church":
        return theme.primary_colors[200];
      case "Conference":
        return theme.primary_colors[600];
      default:
        return theme.primary_colors[500];
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.primary_colors[900],
        }}
      >
        <Typography variant="h6" sx={{ color: theme.primary_colors[100] }}>
          Select Share Template
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: theme.primary_colors[900], pt: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Alert severity="info">
            No templates available for {getCategoryFromServiceType(serviceType)}
            . Click "Create New" to add your first template.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid size={{ xs: 12, sm: 4 }} key={template.id}>
                <Card
                  sx={{
                    backgroundColor:
                      selectedId === template.id
                        ? theme.primary_colors[900]
                        : theme.primary_colors[800],
                    cursor: "pointer",
                    boxShadow: 0,
                    border: `2px solid ${
                      selectedId === template.id
                        ? theme.primary_colors[100]
                        : "transparent"
                    }`,
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: theme.primary_colors[800],
                      transform: "translateY(-2px)",
                    },
                  }}
                  onClick={() => setSelectedId(template.id)}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.primary_colors[100],
                          fontWeight: 600,
                        }}
                      >
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.category}
                        size="small"
                        sx={{
                          backgroundColor: getCategoryColor(template.category),
                          color: theme.primary_colors[900],
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.primary_colors[300],
                        mb: 1,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {template.body_template}
                    </Typography>
                    {/* <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 2,
                      }}
                    >
                      {Object.entries(template.fields)
                        .filter(([_, enabled]) => enabled)
                        .map(([field]) => (
                          <Chip
                            key={field}
                            label={field}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: theme.primary_colors[400],
                              color: theme.primary_colors[200],
                              fontSize: "0.7rem",
                            }}
                          />
                        ))}
                    </Box> */}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: theme.primary_colors[900] }}>
        <Button
          startIcon={<Add />}
          onClick={() => setCreateModalOpen(true)}
          sx={{
            backgroundColor: theme.primary_colors[800],
            color: theme.primary_colors[400],
          }}
        >
          Create New
        </Button>
        <Button
          onClick={handleSelectAndClose}
          variant="contained"
          disabled={!selectedId}
          sx={{
            backgroundColor: theme.primary_colors[400],
            color: theme.primary_colors[900],
          }}
        >
          Use Selected Template
        </Button>
      </DialogActions>

      <CreateTemplateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSave={() => {
          setCreateModalOpen(false);
          const category = getCategoryFromServiceType(serviceType);
          fetchTemplatesByCategory(category);
        }}
      />
    </Dialog>
  );
};

export default SelectTemplateModal;
